import React from "react";
import { useLocation } from "react-router-dom";
import { getHelpForPath } from "../common/helpContent";
import { useTourControls } from "./TourProvider";
import { resetTours } from "../common/tourState";

export default function HelpPanel({ open, onClose }) {
  const location = useLocation();
  const content = getHelpForPath(location.pathname);
  const { hasPageTour, startPageTour, startWelcomeTour } = useTourControls();

  const launch = (start) => {
    onClose();
    // Let the panel finish sliding out before the spotlight measures anything,
    // otherwise it highlights a position the element is about to leave.
    setTimeout(() => start && start(), 220);
  };

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-[60]"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <aside
        className={`fixed top-[57px] right-0 z-[70] h-[calc(100vh-57px)] w-full max-w-sm bg-white shadow-lg border-l border-gray-200 flex flex-col transform transition-transform duration-200 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        aria-hidden={!open}
      >
        <div className="px-6 py-5 border-b border-gray-200 flex items-start justify-between">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-cloudza-600 mb-1">
              Page Help
            </div>
            <h2 className="text-lg font-bold text-gray-900 tracking-tight">{content.title}</h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Close help panel"
            className="ml-4 p-1.5 -mr-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors focus-ring"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-6">
          <p className="text-sm text-gray-700 leading-relaxed">{content.description}</p>

          {hasPageTour && (
            <button
              onClick={() => launch(startPageTour)}
              className="w-full flex items-center gap-3 p-3.5 rounded-xl border border-cloudza-200 bg-cloudza-50 hover:bg-cloudza-100 text-left transition-colors group"
            >
              <span className="w-9 h-9 rounded-full bg-cloudza-600 text-white flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </span>
              <span className="min-w-0">
                <span className="block text-[13px] font-semibold text-cloudza-800">Take a tour of this page</span>
                <span className="block text-xs text-cloudza-700/80">Walks you through the controls, step by step</span>
              </span>
            </button>
          )}

          {content.actions && content.actions.length > 0 && (
            <div>
              <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2.5">On this page</h3>
              <ul className="space-y-2">
                {content.actions.map((action, index) => (
                  <li key={index} className="flex items-start text-sm text-gray-700">
                    <svg
                      className="w-4 h-4 mr-2 mt-0.5 text-cloudza-500 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{action}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 px-6 py-4 space-y-2">
          <button
            onClick={() => launch(startWelcomeTour)}
            className="text-xs font-medium text-gray-500 hover:text-cloudza-600 transition-colors"
          >
            Replay the welcome tour
          </button>
          <button
            onClick={() => {
              resetTours();
              onClose();
            }}
            className="block text-xs text-gray-400 hover:text-gray-600 transition-colors"
          >
            Reset tours — show them again on each page
          </button>
        </div>
      </aside>
    </>
  );
}
